import { ReactNode, useCallback, useMemo, useState } from 'react';

import usePermissions from '~hooks/usePermissions';
import { addItemToPrimitiveArray, removeItemFromPrimitiveArray } from '~services/immutable';
import { postNewsletter } from '../api/calls/postNewsletter';
import { NewsletterContext } from './NewsletterContext';

type NewsletterProviderProps = {
  children: ReactNode;
};

export function NewsletterProvider({ children }: NewsletterProviderProps) {
  const { hasSendChangelogNewsletterPermission } = usePermissions();

  const [selectedChangelogItemIds, setSelectedChangelogItemIds] = useState<Array<ChangelogItem['id']>>([]);
  const [isNewsletterDialogOpen, setIsNewsletterDialogOpen] = useState(false);
  const [isComposingNewsletter, setIsComposingNewsletter] = useState(false);

  const sendNewsletter = useCallback(
    (msg: string) => {
      async function send(message: string, ids: typeof selectedChangelogItemIds) {
        const result = await postNewsletter({
          changeLogIds: ids,
          description: message,
        });
        setIsComposingNewsletter(false);
        setSelectedChangelogItemIds([]);
        return result;
      }
      return send(msg, selectedChangelogItemIds);
    },
    [selectedChangelogItemIds],
  );

  const openNewsletterDialog = useCallback(() => {
    setIsNewsletterDialogOpen(true);
  }, []);

  const closeNewsletterDialog = useCallback(() => {
    setIsNewsletterDialogOpen(false);
  }, []);

  const startComposingNewsletter = useCallback(() => {
    setIsComposingNewsletter(true);
  }, []);

  const cancelComposingNewsletter = useCallback(() => {
    setIsComposingNewsletter(false);
    setSelectedChangelogItemIds([]);
  }, []);

  const toggleSelectedChangelogItemId = useCallback((id: ChangelogItem['id']) => {
    setSelectedChangelogItemIds(ids => {
      const selected = ids.includes(id);
      if (!selected) {
        return addItemToPrimitiveArray(ids, id);
      } else {
        return removeItemFromPrimitiveArray(ids, id);
      }
    });
  }, []);

  const value = useMemo(
    () => ({
      cancelComposingNewsletter,
      canComposeNewsletter: hasSendChangelogNewsletterPermission,
      closeNewsletterDialog,
      isComposingNewsletter,
      isNewsletterDialogOpen,
      openNewsletterDialog,
      selectedChangelogItemIds,
      sendNewsletter,
      startComposingNewsletter,
      toggleSelectedChangelogItemId,
    }),
    [
      cancelComposingNewsletter,
      hasSendChangelogNewsletterPermission,
      closeNewsletterDialog,
      isComposingNewsletter,
      isNewsletterDialogOpen,
      openNewsletterDialog,
      selectedChangelogItemIds,
      sendNewsletter,
      startComposingNewsletter,
      toggleSelectedChangelogItemId,
    ],
  );
  return <NewsletterContext.Provider value={value}>{children}</NewsletterContext.Provider>;
}
